import type { CatalogFacets } from '../../lib/api.js'
import type { Program } from '../../types/index.js'
import { broadcastDayWindow } from '../../lib/broadcast-day.js'
import { effectiveGenreLabel, programReadiness } from './readiness.js'

/**
 * Compteurs de secours calculés sur les fiches déjà chargées.
 *
 * L'API reste la source exacte sur tout le catalogue. Ce repli évite toutefois
 * les tirets indéfinis si le comptage distant tarde ou échoue : l'interface
 * annonce alors explicitement le périmètre chargé au lieu d'inventer un total.
 */
export function catalogFacetsFallback(programs: Program[], total: number): CatalogFacets {
  const genres = new Map<string, CatalogFacets['genres'][number]>()
  let incomplete = 0
  let missingFile = 0
  let genreless = 0
  let available = 0
  let none = 0
  let live = 0
  let incompleteToday = 0
  let archivedCount = 0

  // Journée d'antenne, pas jour civil : une diffusion de 00:30 appartient à la
  // soirée précédente. `broadcastDayWindow` porte déjà la règle et suit
  // l'heure d'été — la borne n'est pas 22:00 UTC toute l'année.
  const { from: dayStart, to: dayEnd } = broadcastDayWindow(new Date())

  for (const program of programs) {
    const readiness = programReadiness(program)
    if (!readiness.ready) incomplete += 1
    if (program.isLive) live += 1
    if (!program.active) archivedCount += 1

    if (!readiness.ready) {
      const airsToday = (program.scheduleItems ?? []).some(item => {
        const start = new Date(item.startTime).getTime()
        return Number.isFinite(start) && start >= dayStart.getTime() && start < dayEnd.getTime()
      })
      if (airsToday) incompleteToday += 1
    }

    const mediaReady = readiness.checks.find(check => check.id === 'media')?.satisfied ?? false
    if (mediaReady) available += 1
    else if (!program.isLive) none += 1

    const media = [
      ...(program.mediaAssets ?? []),
      ...(program.parts ?? []).flatMap(part => part.mediaAssets ?? []),
    ]
    if (media.some(asset => !asset.isProxy && asset.status === 'MISSING')) missingFile += 1

    const genreLabel = effectiveGenreLabel(program)
    if (!genreLabel) {
      genreless += 1
      continue
    }

    const id = program.programGenre?.id ?? `legacy:${genreLabel}`
    const current = genres.get(id)
    genres.set(id, {
      id,
      label: genreLabel,
      color: program.programGenre?.color ?? null,
      sortOrder: current?.sortOrder ?? Number.MAX_SAFE_INTEGER,
      count: (current?.count ?? 0) + 1,
    })
  }

  return {
    total: total || programs.length,
    state: {
      all: total || programs.length,
      incomplete,
      incompleteToday,
      missingFile,
      genreless,
    },
    media: { available, none },
    live,
    // Le repli ne voit que la page chargée : il ne peut pas connaître l'archive
    // hors périmètre. Compter ce qu'il a sous les yeux vaut mieux qu'un zéro,
    // mais ces chiffres restent indicatifs tant que le serveur ne répond pas.
    archive: {
      active:   (total || programs.length) - archivedCount,
      archived: archivedCount,
      all:      total || programs.length,
    },
    genres: [...genres.values()].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'fr')),
  }
}
